/**
 * Resolution-workflow types (milestone 03-13). The workflow is a thin guided
 * flow over the Tier 2.2 worksheet + Tier 3 forms — it owns only progress state
 * and a cached determination chip.
 */

export type WorkflowType = "IA" | "OIC" | "INNOCENT_SPOUSE" | "SPOUSE_RELIEF" | "EXCISE";

export type WorkflowStatus = "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED" | "ABANDONED";

export type WorkflowStep =
  | "personal"
  | "financial"
  | "assets"
  | "banking"
  | "incexp"
  | "summary"
  | "agreement"
  // Excise (03-17) section steps
  | "section1"
  | "section2"
  | "section3"
  | "section4"
  | "section5"
  | "section6"
  | "section7"
  | "section8"
  | "generate";

export type SelectedForm = "" | "9465" | "433-D" | "433-H";

/** The compact determination cached on the workflow (rail chip). */
export interface CachedDetermination {
  available: boolean;
  iaType?: string;
  iaTypeLabel?: string;
  totalDueCents?: number;
  monthlyPaymentCents?: number;
  calculatedMonths?: number;
  warnings?: string[];
  expired?: boolean;
}

export interface ResolutionWorkflow {
  id: string;
  case: string;
  type: WorkflowType;
  typeDisplay: string;
  status: WorkflowStatus;
  statusDisplay: string;
  currentStep: WorkflowStep;
  currentStepDisplay: string;
  selectedForm: SelectedForm;
  determination: CachedDetermination | null;
  createdAt: string;
  updatedAt: string;
}

/** The full determination the step-6 endpoint returns (superset of the cache). */
export interface WorkflowDetermination {
  available: boolean;
  iaType?: string;
  iaTypeLabel?: string;
  iaCondition?: string;
  totalDueCents?: number;
  rmiCents?: number;
  totalAssetsCents?: number;
  monthlyPaymentCents?: number;
  calculatedMonths?: number;
  maxMonths?: number;
  interestRate?: string;
  missingCseds?: number[];
  expiredCseds?: number[];
  warnings: string[];
  expired?: boolean;
}

/** Ordered step definitions for the wizard rail. */
export interface StepDef {
  key: WorkflowStep;
  label: string;
  /** Short rail label. */
  short: string;
}

export const IA_STEPS: StepDef[] = [
  { key: "personal", label: "Personal & Employment", short: "Personal" },
  { key: "financial", label: "Financial Circumstances", short: "Financial" },
  { key: "assets", label: "Assets", short: "Assets" },
  { key: "banking", label: "Banking & Investments", short: "Banking" },
  { key: "incexp", label: "Income & Expenses", short: "Income/Exp" },
  { key: "summary", label: "Summary of Alternatives", short: "Summary" },
  { key: "agreement", label: "Installment Agreement Terms", short: "Terms" },
  { key: "generate", label: "Generate & Attach", short: "Generate" },
];

export const stepIndex = (step: WorkflowStep): number =>
  Math.max(0, IA_STEPS.findIndex((s) => s.key === step));

/** Excise-Tax workflow (03-17): 8 interview sections + the generate step. */
export const EXCISE_STEPS: StepDef[] = [
  { key: "section1", label: "Header & Position", short: "Header" },
  { key: "section2", label: "Responsibility & Authority", short: "Responsibility" },
  { key: "section3", label: "Interview & Handouts", short: "Interview" },
  { key: "section4", label: "EFTPS & Corporate Positions", short: "EFTPS" },
  { key: "section5", label: "Knowledge & Actions", short: "Knowledge" },
  { key: "section6", label: "Third-Party Payer Arrangements", short: "Third-party" },
  { key: "section7", label: "Excise-Tax Responsibility", short: "Excise" },
  { key: "section8", label: "Your Story", short: "Story" },
  { key: "generate", label: "Generate & Attach", short: "Generate" },
];

export const exciseStepIndex = (step: WorkflowStep): number =>
  Math.max(0, EXCISE_STEPS.findIndex((s) => s.key === step));
